import { makeAutoObservable, runInAction, computed } from 'mobx'
import {
  getFirestore,
  writeBatch,
  doc,
  collection,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore'
import { getDocument, updateDocument } from '../../../shared/api/firestore'
import { uploadOrganizationImage } from '../../../shared/api/storage'
import FirebaseApp from '../../../shared/api/firebase'

const initialOrganizationState = {
  id: '',
  name: '',
  type: 'team',
  slug: '',
  description: { ru: '', en: '' },
  imageUrl: '',
  address: '',
  geo: { lat: null, lng: null },
  phone: '',
  countryCode: '',
  regionCode: '',
  city: '',
  timezone: 'UTC',
  currency: 'USD',
  ownerId: '',
  adminIds: [],

  bufferTime: 15,
  autoAddHolidays: true,
  holidayCountry: 'RU',

  subscription: {
    startDate: null,
    endDate: null,
    isActive: false,
    autoRenew: false,
    paymentProvider: '',
    paymentCustomerId: '',
    paymentSubscriptionId: '',
  },
  rating: 0,
  totalReviews: 0,
}

const db = getFirestore(FirebaseApp)

class OrganizationStore {
  organization = initialOrganizationState
  isLoading = true
  isUploading = false
  router = null

  constructor(rootStore) {
    makeAutoObservable(
      this,
      {
        publicUrl: computed,
        hasPremium: computed,
        isSoloMode: computed,
        isTeamMode: computed,
        rootStore: false,
      },
      { autoBind: true },
    )
    this.rootStore = rootStore
    this.errorStore = rootStore.errorStore
    this.authStore = rootStore.authStore // Still needed for createNewOrganization
  }

  clearOrganizationData() {
    this.organization = initialOrganizationState
    this.isLoading = false
  }

  async fetchOrganization(organizationId) {
    this.isLoading = true
    try {
      const orgDoc = await getDocument('organizations', organizationId)
      runInAction(() => {
        if (orgDoc) {
          this.organization = {
            ...initialOrganizationState,
            ...orgDoc,
            id: organizationId,
          }
        }
      })
    } catch (error) {
      console.error('Не удалось загрузить организацию:', error)
      this.errorStore.showError('Не удалось загрузить данные организации')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async updateOrganization(data, showSuccessMessage = true) {
    if (!this.organization.id)
      return { success: false, error: 'Нет ID организации' }
    this.isLoading = true
    try {
      await updateDocument('organizations', this.organization.id, data)
      runInAction(() => {
        this.organization = { ...this.organization, ...data }
        if (showSuccessMessage) {
          this.errorStore.showSuccess('Профиль организации успешно сохранен')
        }
      })
      return { success: true }
    } catch (error) {
      console.error(error)
      this.errorStore.showError('Ошибка при сохранении профиля')
      return { success: false, error }
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async uploadAvatar(blob) {
    if (!this.organization.id || !blob) return
    this.isUploading = true
    try {
      const imageUrl = await uploadOrganizationImage(this.organization.id, blob)
      await this.updateOrganization({ imageUrl }, false) // Do not show success message here
    } catch (error) {
      console.error('Ошибка при загрузке аватара', error)
      this.errorStore.showError('Ошибка при загрузке аватара')
    } finally {
      runInAction(() => {
        this.isUploading = false
      })
    }
  }

  async createNewOrganization(formData) {
    if (!this.authStore.currentUser) {
      return this.errorStore.showError('Вы не авторизованы.')
    }

    if (this.organization && this.organization.id) {
      this.errorStore.showError('У вас уже есть организация.')
      return false
    }

    if (!formData.ownerName || !formData.organizationName || !formData.type) {
      this.errorStore.showError(
        'Пожалуйста, укажите ваше имя, название студии и режим работы.',
      )
      return false
    }

    this.isLoading = true
    const { uid, email, phone } = this.authStore.currentUser
    const ownerNameForStaff = formData.ownerName

    try {
      const batch = writeBatch(db)
      const organizationRef = doc(collection(db, 'organizations'))
      const organizationType = formData.type

      batch.set(organizationRef, {
        // Изменено: Используем finalBusinessType
        name: formData.organizationName,
        type: organizationType,
        slug: organizationRef.id,
        phone: phone || '',
        ownerId: uid,
        adminIds: [uid],
        bufferTime: 15,
        description: { ru: '' },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })

      const staffRef = doc(
        collection(db, 'organizations', organizationRef.id, 'staff'),
      )
      batch.set(staffRef, {
        name: ownerNameForStaff,
        userId: uid,
        avatar: this.authStore.currentUser.photoURL || '',
        email: email,
        phone: phone || '', // Гарантируем, что phone не будет undefined
        position: '',
        bio: { ru: '', en: '' },
        servicesIds: [],
        active: true,
        createdAt: serverTimestamp(),
      })

      const userRef = doc(db, 'users', uid)
      batch.update(userRef, {
        roles: arrayUnion('owner', 'staff'),
        organizationIds: arrayUnion(organizationRef.id),
        staffId: staffRef.id,
        ...(!this.rootStore.userStore.user?.name && {
          name: ownerNameForStaff,
        }),
      })

      await batch.commit()

      await this.rootStore.userStore.loadUserAndOrganization(uid)

      this.errorStore.showSuccess('Ваша организация успешно создана!')
      return true
    } catch (error) {
      console.error('Ошибка при создании организации:', error)
      this.errorStore.showError('Не удалось создать организацию.')
      return false
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  get publicUrl() {
    if (this.organization.username) {
      return `https://booking.catnote.ru/${this.organization.username}`
    }
    return `https://booking.catnote.ru/o/${this.organization.id}`
  }

  get hasPremium() {
    return this.organization.subscription?.isActive === true
  }

  get isSoloMode() {
    return this.organization.type === 'solo'
  }

  get isTeamMode() {
    return this.organization.type === 'team'
  }
}

export default OrganizationStore
